import { useCallback, useEffect, useState } from 'react';
import { ArrowClockwise, Warning } from 'shared/icons';
import Button from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { useToast } from '../ui/Toast';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';
import { DiaIsoParaDate } from '../../constants/coletas';

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD DE COLETAS — reconstruído (02/09) com DADOS 100% LOCAIS (espelho
// `coleta_envios_ml` + tabelas do WMS; nenhuma conexão com banco de terceiro).
//
// Quatro seções, uma pergunta cada:
//   1. HOJE        — "o dia está sob controle?" (semáforo verde/âmbar/vermelho)
//   2. FUNIL       — "onde os envios abertos estão parados?" (barra + legenda)
//   3. OPERAÇÃO    — "picking e bancada estão rodando agora?"
//   4. 7 DIAS      — "como foi a semana?" (enviado × pendente por dia)
// ─────────────────────────────────────────────────────────────────────────────

const FORMATO_DIA_CURTO = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', weekday: 'short' });

/** Cartão-semáforo: o TOM é o alerta (verde ok, âmbar atenção, vermelho agir). */
const CartaoSemaforo = ({ rotulo, valor, tom, ajuda }) => {
    const tons = {
        verde: 'border-success-300 bg-success-soft text-success-text dark:border-success-800 dark:bg-success-900/20',
        ambar: 'border-warning-300 bg-warning-soft text-warning-text dark:border-warning-800 dark:bg-warning-900/20',
        vermelho: 'border-danger-300 bg-danger-soft text-danger-text dark:border-danger-800 dark:bg-danger-900/20',
        neutro: 'border-mono-200 bg-white text-black dark:border-mono-800 dark:bg-mono-950 dark:text-white',
    };
    return (
        <div className={`rounded-2xl border-2 p-4 ${tons[tom] || tons.neutro}`}>
            <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{rotulo}</p>
            <p className="mt-1 font-mono text-4xl font-black tabular-nums">{FormatInt(valor ?? 0)}</p>
            {ajuda ? <p className="mt-1 text-[11px] leading-tight opacity-70">{ajuda}</p> : null}
        </div>
    );
};

const Secao = ({ titulo, pergunta, children }) => (
    <section className="space-y-3">
        <div className="flex items-baseline gap-3 border-b border-mono-200 pb-2 dark:border-mono-800">
            <h3 className="text-sm font-bold uppercase tracking-wide text-black dark:text-white">{titulo}</h3>
            {pergunta ? <span className="text-xs text-mono-500 dark:text-mono-400">{pergunta}</span> : null}
        </div>
        {children}
    </section>
);

// Estágios do funil: rótulo, cor e o que significa (o tooltip do operador).
const ESTAGIOS_FUNIL = [
    { chave: 'aguardando_nf', rotulo: 'Aguardando NF', cor: 'bg-warning-400', texto: 'text-warning-text' },
    { chave: 'sem_etiqueta', rotulo: 'Sem etiqueta ML', cor: 'bg-danger-400', texto: 'text-danger-text' },
    { chave: 'pronto', rotulo: 'Pronto p/ trabalhar', cor: 'bg-info-400', texto: 'text-info-text' },
    { chave: 'em_lote', rotulo: 'Em lote', cor: 'bg-mono-400', texto: 'text-mono-600 dark:text-mono-300' },
    { chave: 'em_os', rotulo: 'Em OS (picking)', cor: 'bg-info-600', texto: 'text-info-text' },
    { chave: 'impresso', rotulo: 'Impresso, aguardando coleta', cor: 'bg-success-500', texto: 'text-success-text' },
];

const CockpitDashboard = () => {
    const toast = useToast();
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            setDados(await api.getColetasDashboard());
        } catch (error) {
            toast.error(error?.message || 'Não foi possível carregar o dashboard.');
        } finally {
            setCarregando(false);
        }
    }, [toast]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    if (carregando && !dados) {
        return <Skeleton className="h-96 w-full" />;
    }
    if (!dados) return null;
    if (dados.habilitado === false) {
        return (
            <div className="rounded-xl border border-warning-200 bg-warning-soft p-4 text-sm text-warning-text">
                <Warning size={16} className="mr-1.5 inline" />
                {dados.motivo}
            </div>
        );
    }

    const { hoje, funil, picking, serie } = dados;
    const maiorDia = Math.max(1, ...serie.map((linha) => linha.meta));

    return (
        <div className="space-y-8">
            <div className="flex justify-end">
                <Button variant="secondary" onClick={carregar} disabled={carregando}>
                    <ArrowClockwise size={16} className="mr-1.5" />
                    Atualizar
                </Button>
            </div>

            <Secao titulo="Hoje" pergunta="o dia está sob controle?">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <CartaoSemaforo rotulo="Para enviar hoje" valor={hoje.meta} tom="neutro" ajuda="Meta do dia (SLA de hoje, sem os cancelados)" />
                    <CartaoSemaforo
                        rotulo="Enviados"
                        valor={hoje.enviados}
                        tom={hoje.meta > 0 && hoje.enviados >= hoje.meta ? 'verde' : 'neutro'}
                        ajuda="Coletados pelo ML"
                    />
                    <CartaoSemaforo
                        rotulo="Pendentes"
                        valor={hoje.pendentes}
                        tom={hoje.pendentes === 0 ? 'verde' : 'ambar'}
                        ajuda="Ainda no galpão"
                    />
                    <CartaoSemaforo
                        rotulo="Atrasados"
                        valor={hoje.atrasados}
                        tom={hoje.atrasados === 0 ? 'verde' : 'vermelho'}
                        ajuda="SLA de dias anteriores, não enviados"
                    />
                </div>
            </Secao>

            <Secao titulo="Funil de preparação" pergunta={`onde os ${FormatInt(funil.total_abertos)} envios abertos estão?`}>
                {/* A barra é UMA: cada segmento é um estágio, na ordem do fluxo. */}
                <div className="flex h-9 w-full overflow-hidden rounded-xl border border-mono-200 dark:border-mono-800">
                    {ESTAGIOS_FUNIL.map((estagio) => {
                        const valor = Number(funil[estagio.chave] ?? 0);
                        if (!valor) return null;
                        const pct = Math.max((valor / Math.max(funil.total_abertos, 1)) * 100, 2.5);
                        return (
                            <div
                                key={estagio.chave}
                                className={`${estagio.cor} flex items-center justify-center overflow-hidden`}
                                style={{ width: `${pct}%` }}
                                title={`${estagio.rotulo}: ${FormatInt(valor)}`}
                            >
                                <span className="truncate px-1 font-mono text-xs font-bold text-white drop-shadow">
                                    {FormatInt(valor)}
                                </span>
                            </div>
                        );
                    })}
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 md:grid-cols-3">
                    {ESTAGIOS_FUNIL.map((estagio) => (
                        <div key={estagio.chave} className="flex items-center gap-2 text-sm">
                            <span className={`h-3 w-3 shrink-0 rounded ${estagio.cor}`} />
                            <span className="text-mono-600 dark:text-mono-400">{estagio.rotulo}</span>
                            <span className={`ml-auto font-mono font-bold tabular-nums ${estagio.texto}`}>
                                {FormatInt(funil[estagio.chave] ?? 0)}
                            </span>
                        </div>
                    ))}
                </div>
            </Secao>

            <Secao titulo="Operação agora" pergunta="picking e bancada estão rodando?">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                    <CartaoSemaforo rotulo="OS liberadas" valor={picking.liberadas} tom={picking.liberadas > 0 ? 'ambar' : 'neutro'} ajuda="Aguardando picker" />
                    <CartaoSemaforo rotulo="Em coleta" valor={picking.em_coleta} tom="neutro" />
                    <CartaoSemaforo rotulo="OS concluídas hoje" valor={picking.concluidas_hoje} tom={picking.concluidas_hoje > 0 ? 'verde' : 'neutro'} />
                    <CartaoSemaforo
                        rotulo="Aguardando embalagem"
                        valor={picking.aguardando_embalagem}
                        tom={picking.aguardando_embalagem > 0 ? 'ambar' : 'verde'}
                        ajuda="Coletados, ainda sem etiqueta"
                    />
                    <CartaoSemaforo rotulo="Bancadas ocupadas" valor={picking.bancadas_ocupadas} tom="neutro" />
                    <CartaoSemaforo rotulo="Embalados hoje" valor={picking.embalados_hoje} tom={picking.embalados_hoje > 0 ? 'verde' : 'neutro'} />
                </div>
            </Secao>

            <Secao titulo="Últimos 7 dias" pergunta="enviado × pendente, por dia de SLA">
                <div className="rounded-2xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <div className="flex items-end justify-between gap-2">
                        {serie.map((linha) => {
                            const alturaEnviados = Math.round((linha.enviados / maiorDia) * 130);
                            const alturaPendentes = Math.round((linha.pendentes / maiorDia) * 130);
                            return (
                                <div key={linha.dia} className="flex flex-1 flex-col items-center gap-1">
                                    <span className="font-mono text-[11px] tabular-nums text-mono-500">
                                        {FormatInt(linha.meta)}
                                    </span>
                                    <div className="flex w-full max-w-[56px] flex-col justify-end" style={{ height: 134 }}>
                                        {alturaPendentes > 0 ? (
                                            <div
                                                className="w-full rounded-t bg-danger-400"
                                                style={{ height: Math.max(alturaPendentes, 3) }}
                                                title={`Pendentes: ${FormatInt(linha.pendentes)}`}
                                            />
                                        ) : null}
                                        <div
                                            className={`w-full bg-success-500 ${alturaPendentes > 0 ? '' : 'rounded-t'}`}
                                            style={{ height: Math.max(alturaEnviados, linha.enviados > 0 ? 3 : 0) }}
                                            title={`Enviados: ${FormatInt(linha.enviados)}`}
                                        />
                                    </div>
                                    <span className="text-[11px] capitalize text-mono-500">
                                        {FORMATO_DIA_CURTO.format(DiaIsoParaDate(linha.dia))}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 flex justify-center gap-6 text-xs text-mono-500">
                        <span className="flex items-center gap-1.5">
                            <span className="h-3 w-3 rounded bg-success-500" /> Enviados
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="h-3 w-3 rounded bg-danger-400" /> Pendentes
                        </span>
                    </div>
                </div>
            </Secao>
        </div>
    );
};

export default CockpitDashboard;
